import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Coupons from './parts/Coupons/Coupons';
import {
  EmptyState,
  Loading,
  PageSubTitle,
  PaginationGroup,
  TableTitle
} from '../../common/Elements/Elements';
import BillingTable from './BillingTable/BillingTable';
import Widgets from './parts/Widgets/Widgets';

import { postRequest } from 'utils/requests';
import { initialTableShow } from 'utils/settings';
import { getBillingList } from './helpers/helpers';

import classes from './Billing.module.scss';

const Billing = ({ customerId }) => {
  const [billing, setBilling] = useState('');
  const [billingList, setBillingList] = useState('');
  const [loader, setLoader] = useState(true);
  const [dataConection, setDataConection] = useState(false);
  const [fieldName, setFieldName] = useState('amount');
  const [direction, setDirection] = useState(initialTableShow.direction * -1);
  const [currentPage, setCurrentPage] = useState(initialTableShow.currentPage);

  const { t } = useTranslation('activity');

  const itemsPerPage = initialTableShow.itemsPerPage;
  const paginationLimit = 100;

  const { contact, collection, invoices } = billing;
  const widgets = {
    collection: collection,
    contact: contact
  };

  useEffect(() => {
    let cancelled = false;
    const url = '/customer/billing?limit=' + paginationLimit;
    const REQ_BODY = {
      customerId: customerId
    };
    postRequest(url, REQ_BODY)
      .then(res => {
        if (!cancelled) {
          // A Chargebee failure comes back as HTTP 400 and postRequest rejects
          // on it, so this block only ever sees a 2xx - the guard below is for a
          // success body that arrives without `invoices`, which getBillingList
          // would then call .sort() on.
          // Every invoice Chargebee returns is shown: the previous
          // `total > 0` filter silently dropped zero-amount invoices, credits,
          // and any invoice whose total arrived as the string 'NA' (the API maps
          // a null total to that), which emptied the table for tenants whose
          // invoices are all non-positive.
          res.body = {
            ...res.body,
            invoices: Array.isArray(res.body && res.body.invoices)
              ? res.body.invoices
              : []
          };
          setBilling(res.body);
        }
      })
      .catch(err => {
        console.log(err);
        if (!cancelled) {
          setLoader(false);
          setDataConection(false);
        }
      });
    return () => (cancelled = true);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (billing) {
      setLoader(false);
      setDataConection(true);
      setBillingList(
        getBillingList(
          invoices,
          direction,
          fieldName,
          currentPage,
          itemsPerPage
        )
      );
    }
    // eslint-disable-next-line
  }, [billing, customerId, currentPage, itemsPerPage, direction, fieldName]);

  const handleSortable = field => () => {
    if (field === fieldName) {
      const directionTemp = direction * -1;
      setDirection(directionTemp);
    } else {
      setFieldName(field);
    }
  };

  if (loader) {
    return <Loading />;
  }

  // A failed request is not the same as a customer with no billing. Every other
  // page in the app reports a request failure with the "connect" empty state,
  // and reading "no billing information" here sent the Chargebee outage in
  // P81-133660 looking for a data problem instead of a configuration one.
  if (!dataConection) {
    return <EmptyState message="connect" />;
  }

  return (
    <>
      <PageSubTitle hiden title="Biling informaton" />
      <Widgets widgets={widgets} />
      {/*
       * `coupons` arrives as `false` when there are none, so reading `.length`
       * off it was already yielding undefined - harmless, but it throws outright
       * if the key is ever absent. Guarding the access keeps the empty-array
       * case rendering nothing, which `Boolean(coupons)` alone would not.
       */}
      {billing.coupons && billing.coupons.length !== 0 && (
        <Coupons data={billing.coupons} />
      )}
      {/*
       * A tenant with no invoices is an empty state, not a failure, so the
       * invoices box stays on screen and says so from the inside - the same
       * shape every other table in the app uses. Swapping the whole section for
       * a placeholder made the page read as broken, which is how this ticket
       * came to be filed against Chargebee (P81-133660).
       */}
      <TableTitle
        className={classes.tableTitle}
        title={t('activity:billing.INVOICES')}
        additional={invoices.length}
        withWordResult={false}
      />
      <BillingTable
        tableData={billingList.invoices}
        customerId={customerId}
        handleSortable={handleSortable}
        fieldName={fieldName}
        direction={direction}
      />

      {billingList && billingList.size > 10 && billingList.totalPages > 1 && (
        <PaginationGroup
          pageCount={billingList.totalPages}
          setPage={setCurrentPage}
          currentPage={currentPage}
          itemsTotal={billingList.size}
          itemsPerPage={itemsPerPage}
        />
      )}
    </>
  );
};

Billing.propTypes = {
  customerId: PropTypes.string
};

export default Billing;
